import React from 'react';
import styled from 'styled-components';
import moment from 'moment-jalaali';
import { Input, message } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

import { useGetDailyNoteQuery, useAddDailyNoteMutation, useUpdateDailyNoteMutation } from 'graphql/types';
// import { zMonth, zYear } from '@polgozar/utils';

import { DailyNotesCard, DailyNotesHeader, DailyNotesTitle, DailyNotesTitleRow, NoteDate, DailyNotesPasscode, DailyNotesActions, IconButton, ContentBox, ContentText, SaveRow, ArrowButtons, TextButton } from './index.styled';
import { Button } from 'components/elements';

const { TextArea } = Input;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type DailyMeetingNotesProps = {
  eventId: string;
  eventStartTime?: string | null;
  canEdit?: boolean;
  passcode?: string | null;
};

// function formatZoroastrianMonth(date: moment.Moment) {
//   const m = zMonth(date);
//   const y = zYear(date);
//   return `${m.name.en} ${y} - Zoroastrian Calendar`;
// }

function toDayStart(date: moment.Moment) {
  return date.clone().startOf('day');
}

const DailyMeetingNotes: React.FC<DailyMeetingNotesProps> = ({ eventId, eventStartTime, canEdit = false, passcode }) => {
  const { data, loading, refetch } = useGetDailyNoteQuery({
    variables: {
      filters: {
        event: { id: { eq: String(eventId) } },
      },
    },
  });

  const notes = data?.dailyNotes?.data ?? [];

  const sortedNotes = React.useMemo(() => {
    return [...notes].sort((a, b) => {
      const aTime = a.attributes?.meetingDate ?? '';
      const bTime = b.attributes?.meetingDate ?? '';
      return aTime.localeCompare(bTime);
    });
  }, [notes]);

  // Use primitive deps to prevent "reset state" effects from running on every render
  // (Apollo may return new array/object references even when the data is effectively unchanged).
  const firstNoteDate = sortedNotes[0]?.attributes?.meetingDate ?? null;
  const initialSelectedDate = React.useMemo(() => {
    if (firstNoteDate) return toDayStart(moment(firstNoteDate));
    if (eventStartTime) return toDayStart(moment(eventStartTime));
    return toDayStart(moment());
  }, [firstNoteDate, eventStartTime]);

  const [selectedDate, setSelectedDate] = React.useState<moment.Moment>(initialSelectedDate);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const initialSelectedDateMs = initialSelectedDate.valueOf();
  React.useEffect(() => {
    // When the query results arrive (or event changes), reset selection.
    // Depend on timestamp, not the moment object reference.
    setSelectedDate(moment(initialSelectedDateMs));
    setIsEditing(false);
  }, [initialSelectedDateMs]);

  const noteForDay = React.useMemo(() => {
    const dayStart = toDayStart(selectedDate);
    return sortedNotes.find((n) => {
      const meetingDate = n.attributes?.meetingDate;
      if (!meetingDate) return false;
      return toDayStart(moment(meetingDate)).isSame(dayStart, 'day');
    });
  }, [selectedDate, sortedNotes]);

  const noteForDayId = noteForDay?.id;
  const noteForDayContent = noteForDay?.attributes?.content ?? '';

  React.useEffect(() => {
    setDraft(noteForDayContent);
  }, [noteForDayId, noteForDayContent]);

  // const zoroLabel = formatZoroastrianMonth(selectedDate);
  const gregorianLabel = selectedDate.format('YYYY.MM.DD');

  const selectedDayNoteId = noteForDay?.id ? String(noteForDay.id) : null;

  const [addDailyNote, { loading: adding }] = useAddDailyNoteMutation();
  const [updateDailyNote, { loading: updating }] = useUpdateDailyNoteMutation();

  const handleChangeDay = (deltaDays: number) => {
    setIsEditing(false);
    setSelectedDate((prev) => toDayStart(prev).add(deltaDays, 'day'));
  };

  const handleStartEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    const content = draft.trim();
    if (!content) {
      message.error('Please write daily meeting notes first.');
      return;
    }

    const meetingDateIso = toDayStart(selectedDate).toISOString();
    try {
      if (selectedDayNoteId) {
        await updateDailyNote({
          variables: {
            dailyNoteId: selectedDayNoteId,
            input: {
              event: eventId,
              meetingDate: meetingDateIso,
              content,
            },
          },
        });
      } else {
        await addDailyNote({
          variables: {
            input: {
              event: eventId,
              meetingDate: meetingDateIso,
              content,
            },
          },
        });
      }
      setIsEditing(false);
      await refetch();
      message.success('Daily meeting notes saved.');
    } catch (e) {
      message.error('Failed to save daily meeting notes.');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return (
    <DailyNotesCard>
      <DailyNotesHeader>
        <div>
          <DailyNotesTitleRow>
            <DailyNotesTitle>Daily Meeting Note </DailyNotesTitle>
          </DailyNotesTitleRow>
          {passcode ? <DailyNotesPasscode>Meeting Passcode: {passcode}</DailyNotesPasscode> : null}
        </div>

        <DailyNotesActions>
          <ArrowButtons>
            <IconButton
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => handleChangeDay(-1)}
              disabled={adding || updating}
            />
            <IconButton
              type="default"
              icon={<ArrowRightOutlined />}
              onClick={() => handleChangeDay(1)}
              disabled={adding || updating}
            />
          </ArrowButtons>

          {noteForDay && canEdit ? (
            isEditing ? (
              <TextButton type="default" onClick={handleSave} loading={adding || updating}>
                Save
              </TextButton>
            ) : (
              <Button type="default" onClick={handleStartEdit} icon={<EditOutlined />}>
                Edit
              </Button>
            )
          ) : null}

          {!noteForDay ? (
            canEdit ? (
              isEditing ? (
                <TextButton type="default" onClick={handleSave} loading={adding || updating}>
                  Send
                </TextButton>
              ) : (
                <TextButton type="default" onClick={handleStartEdit} icon={<PlusOutlined />}>
                  Add new Note
                </TextButton>
              )
            ) : null
          ) : null}
        </DailyNotesActions>
      </DailyNotesHeader>

      {loading ? (
        <Centered>Loading daily notes...</Centered>
      ) : (
        <ContentBox>
          {isEditing && canEdit ? (
            <>
              <NoteDate>{gregorianLabel}</NoteDate>
              <TextArea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                placeholder="Write your daily meeting notes here..."
              />
              <SaveRow>
                <TextButton type="default" onClick={() => setIsEditing(false)}>
                  Cancel
                </TextButton>
              </SaveRow>
            </>
          ) : noteForDay ? (
            <>
              <NoteDate>{gregorianLabel}</NoteDate>
              <ContentText>{noteForDay.attributes?.content ?? ''}</ContentText>
            </>
          ) : (
            <ContentText>No notes for this day yet.</ContentText>
          )}
        </ContentBox>
      )}
    </DailyNotesCard>
  );
};

export default DailyMeetingNotes;

